import type Phaser from "phaser";
import { textStyle } from "./textStyles";

export interface MenuItem {
  label: string;
  /** Если задано — выбор сразу резолвит callback. Иначе — нужно onPick. */
  payload: string;
  /** Серый/недоступный пункт. */
  disabled?: boolean;
}

/**
 * Простое меню списка пунктов с курсором ►.
 * Управление: стрелки + Enter, мышь.
 */
export default class ActionMenu {
  private container: Phaser.GameObjects.Container;
  private cursorImg!: Phaser.GameObjects.Image;
  private items: MenuItem[] = [];
  private texts: Phaser.GameObjects.Text[] = [];
  private cursorIdx = 0;
  private resolveFn?: (item: MenuItem) => void;
  private upHandler?: () => void;
  private downHandler?: () => void;
  private enterHandler?: () => void;
  /** Уведомление о смене подсветки — для бокса описания. */
  private hoverFn?: (item: MenuItem) => void;

  constructor(
    private scene: Phaser.Scene,
    public readonly x: number,
    public readonly y: number,
    private columns = 2,
  ) {
    this.container = scene.add.container(x, y);
    this.container.setDepth(101);
    this.container.setVisible(false);

    const frame = scene.add.image(0, 0, "ui_menu_box").setOrigin(0, 0);
    this.container.add(frame);
  }

  setItems(items: MenuItem[]): void {
    this.items = items;
    this.texts.forEach((t) => t.destroy());
    this.texts = [];
    this.cursorIdx = 0;

    items.forEach((it, i) => {
      const col = i % this.columns;
      const row = Math.floor(i / this.columns);
      const tx = 16 + col * 92;
      const ty = 12 + row * 26;
      const t = this.scene.add.text(tx, ty, it.label, textStyle(8, it.disabled ? "#999999" : "#000000"));
      this.container.add(t);
      this.texts.push(t);
    });

    if (!this.cursorImg) {
      this.cursorImg = this.scene.add.image(0, 0, "ui_cursor").setOrigin(0, 0);
      this.container.add(this.cursorImg);
    }
    this.updateCursor();
  }

  open(onHover?: (item: MenuItem) => void): Promise<MenuItem> {
    this.container.setVisible(true);
    this.hoverFn = onHover;
    this.attachInput();
    // Сразу сообщить текущее наведение, чтобы бокс описания заполнился.
    if (this.hoverFn && this.items[this.cursorIdx]) {
      this.hoverFn(this.items[this.cursorIdx]);
    }
    return new Promise((resolve) => {
      this.resolveFn = resolve;
    });
  }

  close(): void {
    this.container.setVisible(false);
    this.detachInput();
    this.hoverFn = undefined;
  }

  private attachInput(): void {
    const kb = this.scene.input.keyboard;
    if (!kb) return;
    this.upHandler = () => this.move(-1);
    this.downHandler = () => this.move(1);
    this.enterHandler = () => this.confirm();
    kb.on("keydown-UP", this.upHandler);
    kb.on("keydown-DOWN", this.downHandler);
    kb.on("keydown-LEFT", this.upHandler);
    kb.on("keydown-RIGHT", this.downHandler);
    kb.on("keydown-ENTER", this.enterHandler);
    kb.on("keydown-SPACE", this.enterHandler);

    this.texts.forEach((t, i) => {
      t.setInteractive({ useHandCursor: true });
      t.on("pointerdown", () => {
        this.cursorIdx = i;
        this.updateCursor();
        this.confirm();
      });
      t.on("pointerover", () => {
        this.cursorIdx = i;
        this.updateCursor();
        if (this.hoverFn) this.hoverFn(this.items[this.cursorIdx]);
      });
    });
  }

  private detachInput(): void {
    const kb = this.scene.input.keyboard;
    if (!kb) return;
    if (this.upHandler) {
      kb.off("keydown-UP", this.upHandler);
      kb.off("keydown-LEFT", this.upHandler);
    }
    if (this.downHandler) {
      kb.off("keydown-DOWN", this.downHandler);
      kb.off("keydown-RIGHT", this.downHandler);
    }
    if (this.enterHandler) {
      kb.off("keydown-ENTER", this.enterHandler);
      kb.off("keydown-SPACE", this.enterHandler);
    }
    this.texts.forEach((t) => {
      t.disableInteractive();
      t.removeAllListeners();
    });
  }

  private move(delta: number): void {
    if (!this.items.length) return;
    let next = this.cursorIdx;
    for (let i = 0; i < this.items.length; i++) {
      next = (next + delta + this.items.length) % this.items.length;
      if (!this.items[next].disabled) {
        this.cursorIdx = next;
        break;
      }
    }
    this.updateCursor();
    if (this.hoverFn) this.hoverFn(this.items[this.cursorIdx]);
  }

  private updateCursor(): void {
    if (!this.cursorImg || !this.texts.length) return;
    const t = this.texts[this.cursorIdx];
    this.cursorImg.setPosition(t.x - 10, t.y + 1);
  }

  private confirm(): void {
    const item = this.items[this.cursorIdx];
    if (!item || item.disabled) return;
    const r = this.resolveFn;
    this.resolveFn = undefined;
    this.close();
    if (r) r(item);
  }
}
